import React, { useEffect, useState } from "react";

type ActivityBarLayout = "compact" | "expanded" | "overlay";

interface ActivityLayoutButtonProps {
  layout: ActivityBarLayout;
  isSelected: boolean;
  onSelect: (layout: ActivityBarLayout) => void;
}

const getLayoutInfo = (layout: ActivityBarLayout, colors: any) => {
  const {
    mapBg,
    waveColor,
    barBg,
    barBorder,
    metricBg,
    textPrimary,
    textSecondary,
    accentBg,
    textOutline,
  } = colors;

  // Consistent blur and opacity for all activity bars
  const ACTIVITY_BAR_BLUR = "3";
  const ACTIVITY_BAR_OPACITY = "0.7";

  // Shared terrain background component
  const TerrainBackground = ({
    id,
    height,
  }: {
    id: string;
    height: number;
  }) => (
    <>
      {/* Satellite-style map background */}
      <defs>
        <radialGradient id={`terrain-${id}`} cx="40%" cy="40%">
          <stop offset="0%" stopColor="#7a9b5f" />
          <stop offset="50%" stopColor="#6b8e4e" />
          <stop offset="100%" stopColor="#5a7a3d" />
        </radialGradient>
        <filter id={`blur-${id}`}>
          <feGaussianBlur in="SourceGraphic" stdDeviation={ACTIVITY_BAR_BLUR} />
        </filter>
      </defs>
      <g id={`terrain-bg-${id}`}>
        {/* Base terrain */}
        <rect
          x="0"
          y="0"
          width="120"
          height={height}
          fill={`url(#terrain-${id})`}
        />
        {/* Rolling hills contours */}
        <path
          d="M0,15 Q30,10 60,18 T120,15"
          fill="none"
          stroke="#8fae6f"
          strokeWidth="1.5"
          opacity="0.4"
        />
        <path
          d="M0,25 Q40,22 80,28 T120,24"
          fill="none"
          stroke="#8fae6f"
          strokeWidth="1.5"
          opacity="0.4"
        />
        <path
          d="M0,35 Q30,32 70,38 T120,33"
          fill="none"
          stroke="#8fae6f"
          strokeWidth="1.5"
          opacity="0.3"
        />
        <path
          d="M0,45 Q30,42 70,50 T120,46"
          fill="none"
          stroke="#8fae6f"
          strokeWidth="1.5"
          opacity="0.25"
        />
        {/* Stream/water */}
        <path
          d="M10,0 Q20,15 25,30 T35,{height}"
          fill="none"
          stroke="#4a9cc7"
          strokeWidth="2.5"
          opacity="0.6"
        />
        <path
          d="M12,0 Q22,15 27,30 T37,{height}"
          fill="none"
          stroke="#6bb8e0"
          strokeWidth="1"
          opacity="0.4"
        />
        {/* Road with branch */}
        <path
          d={`M80,0 L85,${height * 0.6} L90,${height}`}
          fill="none"
          stroke="#a0a0a0"
          strokeWidth="2"
          opacity="0.7"
        />
        <path
          d="M85,20 L100,18"
          fill="none"
          stroke="#a0a0a0"
          strokeWidth="1.5"
          opacity="0.6"
        />
      </g>
    </>
  );

  switch (layout) {
    case "expanded":
      return {
        name: "Expanded",
        description: "Full-size metrics",
        preview: (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 120 80"
            style={{ display: "block" }}
          >
            <TerrainBackground id="expanded" height={80} />

            {/* Expanded activity bar - backdrop blur effect */}
            <defs>
              <clipPath id="bar-mask-expanded">
                <rect x="2" y="52" width="116" height="26" rx="4" />
              </clipPath>
            </defs>
            {/* Blurred backdrop */}
            <g clipPath="url(#bar-mask-expanded)">
              <use href="#terrain-bg-expanded" filter="url(#blur-expanded)" />
            </g>
            {/* Semi-transparent overlay */}
            <rect
              x="2"
              y="52"
              width="116"
              height="26"
              fill={barBg}
              opacity={ACTIVITY_BAR_OPACITY}
              rx="4"
            />

            {/* Metrics - bottom-aligned labels, speed number taller */}
            <text
              x="19"
              y="65"
              fontSize="6"
              fill={textPrimary}
              textAnchor="middle"
              fontWeight="600"
            >
              5.2
            </text>
            <text
              x="19"
              y="72"
              fontSize="5"
              fill={textSecondary}
              textAnchor="middle"
            >
              mi
            </text>

            <text
              x="50"
              y="62"
              fontSize="9"
              fill={textPrimary}
              textAnchor="middle"
              fontWeight="600"
            >
              12.3
            </text>
            <text
              x="50"
              y="72"
              fontSize="5"
              fill={textSecondary}
              textAnchor="middle"
            >
              mph
            </text>

            <text
              x="81"
              y="65"
              fontSize="6"
              fill={textPrimary}
              textAnchor="middle"
              fontWeight="600"
            >
              25:14
            </text>
            <text
              x="81"
              y="72"
              fontSize="5"
              fill={textSecondary}
              textAnchor="middle"
            >
              time
            </text>

            <rect x="98" y="55" width="17" height="20" fill={accentBg} rx="2" />
            <text
              x="106.5"
              y="67"
              fontSize="8"
              fill={textPrimary}
              textAnchor="middle"
            >
              ⏸
            </text>
          </svg>
        ),
      };
    case "compact":
      return {
        name: "Compact",
        description: "Space-saving design",
        preview: (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 120 80"
            style={{ display: "block" }}
          >
            <TerrainBackground id="compact" height={80} />

            {/* Compact activity bar - backdrop blur effect */}
            <defs>
              <clipPath id="bar-mask-compact">
                <rect x="2" y="65" width="116" height="13" rx="4" />
              </clipPath>
            </defs>
            {/* Blurred backdrop */}
            <g clipPath="url(#bar-mask-compact)">
              <use href="#terrain-bg-compact" filter="url(#blur-compact)" />
            </g>
            {/* Semi-transparent overlay */}
            <rect
              x="2"
              y="65"
              width="116"
              height="13"
              fill={barBg}
              opacity={ACTIVITY_BAR_OPACITY}
              rx="4"
            />

            {/* Compact metrics - distance, speed, time */}
            <text
              x="8"
              y="73.5"
              fontSize="6"
              fill={textPrimary}
              fontWeight="600"
            >
              5.2 mi
            </text>
            <text
              x="60"
              y="73.5"
              fontSize="6"
              fill={textPrimary}
              fontWeight="600"
              textAnchor="middle"
            >
              12.3 mph
            </text>
            <text
              x="112"
              y="73.5"
              fontSize="6"
              fill={textPrimary}
              fontWeight="600"
              textAnchor="end"
            >
              time 25:14
            </text>

            <rect x="107" y="67" width="9" height="9" fill={accentBg} rx="1" />
            <text
              x="111.5"
              y="74"
              fontSize="6"
              fill={textPrimary}
              textAnchor="middle"
            >
              ⏸
            </text>
          </svg>
        ),
      };
    case "overlay":
      return {
        name: "Overlay",
        description: "Speed centered over map",
        preview: (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 120 80"
            style={{ display: "block" }}
          >
            <TerrainBackground id="overlay" height={80} />

            {/* Center: Large speed extending above bar with bold text outline */}
            <text
              x="60"
              y="63"
              fontSize="16"
              fill={textOutline}
              textAnchor="middle"
              fontWeight="700"
              stroke={textOutline}
              strokeWidth="2.5"
            >
              12<tspan fontSize="8">.3</tspan>
            </text>
            <text
              x="60"
              y="63"
              fontSize="16"
              fill={textPrimary}
              textAnchor="middle"
              fontWeight="700"
            >
              12<tspan fontSize="8">.3</tspan>
            </text>

            {/* Overlay bar - backdrop blur effect */}
            <defs>
              <clipPath id="bar-mask-overlay">
                <rect x="2" y="65" width="116" height="13" rx="4" />
              </clipPath>
            </defs>
            {/* Blurred backdrop */}
            <g clipPath="url(#bar-mask-overlay)">
              <use href="#terrain-bg-overlay" filter="url(#blur-overlay)" />
            </g>
            {/* Semi-transparent overlay */}
            <rect
              x="2"
              y="65"
              width="116"
              height="13"
              fill={barBg}
              opacity={ACTIVITY_BAR_OPACITY}
              rx="4"
            />

            {/* Left: Distance */}
            <text
              x="12"
              y="73.5"
              fontSize="6"
              fill={textPrimary}
              fontWeight="600"
            >
              5.2 mi
            </text>

            {/* Right: Time */}
            <text
              x="108"
              y="73.5"
              fontSize="6"
              fill={textPrimary}
              fontWeight="600"
              textAnchor="end"
            >
              25:14
            </text>

            {/* MPH label in bar */}
            <text
              x="60"
              y="73"
              fontSize="5"
              fill={textSecondary}
              textAnchor="middle"
              fontWeight="700"
            >
              MPH
            </text>
          </svg>
        ),
      };
    default:
      return { name: layout, description: "", preview: null };
  }
};

export const ActivityLayoutButton: React.FC<ActivityLayoutButtonProps> = ({
  layout,
  isSelected,
  onSelect,
}) => {
  const [colors, setColors] = useState({
    mapBg: "rgba(80,140,80,0.2)",
    waveColor: "rgba(100,180,100,0.4)",
    barBg: "rgba(0,0,0,0.5)",
    barBorder: "rgba(255,255,255,0.2)",
    metricBg: "rgba(255,255,255,0.1)",
    textPrimary: "rgba(255,255,255,0.95)",
    textSecondary: "rgba(255,255,255,0.6)",
    accentBg: "rgba(255,100,100,0.3)",
    textOutline: "rgba(0,0,0,0.9)",
  });

  useEffect(() => {
    const updateColors = () => {
      const styles = getComputedStyle(document.documentElement);
      const getValue = (varName: string, fallback: string) => {
        const value = styles.getPropertyValue(varName).trim();
        return value || fallback;
      };

      setColors({
        mapBg: getValue("--bg-subtle", "rgba(80,140,80,0.2)"),
        waveColor: getValue("--text-secondary", "rgba(100,180,100,0.4)"),
        barBg: getValue("--bg-glass-strong", "rgba(0,0,0,0.5)"),
        barBorder: getValue("--border", "rgba(255,255,255,0.2)"),
        metricBg: getValue("--bg-elevated", "rgba(255,255,255,0.1)"),
        textPrimary: getValue("--text-primary", "rgba(255,255,255,0.95)"),
        textSecondary: getValue("--text-secondary", "rgba(255,255,255,0.6)"),
        accentBg: getValue("--danger", "rgba(255,100,100,0.3)"),
        textOutline: getValue("--bg-primary", "rgba(0,0,0,0.9)"),
      });
    };

    updateColors();

    // Watch for theme changes
    const observer = new MutationObserver(updateColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });

    // Also listen for manual theme updates
    window.addEventListener("themechange", updateColors);

    return () => {
      observer.disconnect();
      window.removeEventListener("themechange", updateColors);
    };
  }, []);

  const info = getLayoutInfo(layout, colors);

  return (
    <button
      onClick={() => onSelect(layout)}
      title={`${info.name} - ${info.description}`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0",
        width: "100%",
        background: isSelected ? "rgba(62, 106, 225, 0.3)" : "var(--bg-subtle)",
        border: isSelected
          ? "2px solid var(--accent)"
          : "1px solid var(--border)",
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s",
        overflow: "hidden",
      }}
    >
      {/* Preview area */}
      <div
        style={{
          width: "100%",
          aspectRatio: "3/2",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {info.preview}
      </div>

      {/* Label area */}
      <div
        style={{
          width: "100%",
          padding: "6px 4px",
          background: isSelected
            ? "rgba(62, 106, 225, 0.2)"
            : "rgba(0,0,0,0.2)",
          borderTop: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2px",
        }}
      >
        <div
          style={{
            fontSize: "10px",
            color: isSelected ? "var(--text-primary)" : "var(--text-secondary)",
            fontWeight: isSelected ? 600 : 500,
            whiteSpace: "nowrap",
          }}
        >
          {info.name}
        </div>
        <div
          style={{
            fontSize: "8px",
            color: "var(--text-secondary)",
            whiteSpace: "nowrap",
          }}
        >
          {info.description}
        </div>
      </div>
    </button>
  );
};
